import { useEffect, useMemo, useState } from "react";
import {
  FiPlus,
  FiSearch,
  FiDownload,
  FiEye,
  FiRotateCcw,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const statusStyles = {
  DRAFT: "bg-orange-100 text-orange-700",
  DISPATCHED: "bg-green-100 text-green-700",
  CLOSED: "bg-blue-100 text-blue-700",
  CANCELLED: "bg-red-100 text-red-700",
};

export default function SupplierReturnsPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [returns, setReturns] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadReturns() {
      try {
        setIsLoading(true);
        setError("");

        const params = new URLSearchParams();
        if (search.trim()) params.set("search", search.trim());
        if (status) params.set("status", status);

        const response = await apiRequest(
          `/purchases/returns${params.toString() ? `?${params}` : ""}`,
          { accessToken },
        );

        setReturns(Array.isArray(response) ? response : []);
      } catch (requestError) {
        setError(requestError.message || "Could not load supplier returns.");
      } finally {
        setIsLoading(false);
      }
    }

    const timer = setTimeout(() => {
      if (accessToken) loadReturns();
    }, 250);

    return () => clearTimeout(timer);
  }, [accessToken, search, status]);

  const filteredReturns = useMemo(() => {
    if (!selectedDate) return returns;

    return returns.filter(
      (item) =>
        new Date(item.returnDate || item.createdAt)
          .toISOString()
          .slice(0, 10) === selectedDate,
    );
  }, [returns, selectedDate]);

  const stats = useMemo(() => {
    const totalValue = filteredReturns.reduce(
      (sum, item) => sum + Number(item.commercialTotal || 0),
      0,
    );

    return {
      total: filteredReturns.length,
      draft: filteredReturns.filter((item) => item.status === "DRAFT").length,
      dispatched: filteredReturns.filter(
        (item) => item.status === "DISPATCHED",
      ).length,
      totalValue,
    };
  }, [filteredReturns]);

  const formatDate = (value) =>
    value
      ? new Date(value).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;

  const exportCsv = () => {
    const rows = [
      ["Return Number", "GRN", "Supplier", "Date", "Items", "Product Value (Excl. Freight)", "Status"],
      ...filteredReturns.map((item) => [
        item.returnNumber,
        item.goodsReceipt?.grnNumber || "",
        item.supplier?.displayName || "",
        formatDate(item.returnDate || item.createdAt),
        item._count?.items || 0,
        item.commercialTotal || 0,
        item.status,
      ]),
    ];

    const csv = rows
      .map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");

    const link = document.createElement("a");
    link.href = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    );
    link.download = "supplier-returns.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Purchasing / Supplier Returns</p>
          <h1 className="mt-1 text-3xl font-bold">Supplier Returns</h1>
          <p className="mt-2 text-gray-500">
            Manage damaged, defective and incorrect inventory returns.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={exportCsv}
            className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-5"
          >
            <FiDownload />
            Export
          </button>

          <button
            type="button"
            onClick={() =>
              navigate("/admindashboard/purchases/returns/create")
            }
            className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 text-white"
          >
            <FiPlus />
            Create Return
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-6 flex gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="mb-6 grid gap-5 lg:grid-cols-4">
        <StatCard title="Total Returns" value={stats.total} icon={FiRotateCcw} color="blue" />
        <StatCard title="Draft" value={stats.draft} icon={FiClock} color="orange" />
        <StatCard title="Dispatched" value={stats.dispatched} icon={FiCheckCircle} color="green" />
        <StatCard title="Return Value" value={formatCurrency(stats.totalValue)} icon={FiAlertTriangle} color="red" />
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-5">
        <div className="grid gap-4 lg:grid-cols-4">
          <div className="relative lg:col-span-2">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search Return / GRN / Supplier"
              className="h-12 w-full rounded-xl border border-gray-200 pl-11 pr-4"
            />
          </div>

          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">All Status</option>
            <option value="DRAFT">Draft</option>
            <option value="DISPATCHED">Dispatched</option>
            <option value="CLOSED">Closed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>

          <input
            type="date"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-6 py-4 text-left">Return No</th>
                <th className="px-6 py-4 text-left">GRN</th>
                <th className="px-6 py-4 text-left">Supplier</th>
                <th className="px-6 py-4 text-left">Date</th>
                <th className="px-6 py-4 text-center">Items</th>
                <th className="px-6 py-4 text-right">Product Value</th>
                <th className="px-6 py-4 text-center">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {!isLoading &&
                filteredReturns.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4 font-semibold">{item.returnNumber}</td>
                    <td className="px-6 py-4">{item.goodsReceipt?.grnNumber || "—"}</td>
                    <td className="px-6 py-4">
                      <p>{item.supplier?.displayName || "—"}</p>
                      <p className="mt-1 text-xs text-gray-400">{item.store?.name || ""}</p>
                    </td>
                    <td className="px-6 py-4">{formatDate(item.returnDate || item.createdAt)}</td>
                    <td className="px-6 py-4 text-center">{item._count?.items || 0}</td>
                    <td className="px-6 py-4 text-right font-semibold">
                      {formatCurrency(item.commercialTotal)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <StatusBadge status={item.status} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() =>
                            navigate(`/admindashboard/purchases/returns/${item.id}`)
                          }
                          className="flex h-9 items-center gap-2 rounded-lg bg-primary-50 px-3 font-medium text-primary-700"
                        >
                          <FiEye />
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

              {!isLoading && filteredReturns.length === 0 && (
                <tr>
                  <td colSpan="8" className="px-6 py-16 text-center text-sm text-gray-500">
                    No supplier returns found.
                  </td>
                </tr>
              )}

              {isLoading && (
                <tr>
                  <td colSpan="8" className="px-6 py-16 text-center text-sm text-gray-500">
                    Loading supplier returns...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusStyles[status] || "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    orange: "bg-orange-50 text-orange-600",
    red: "bg-red-50 text-red-600",
  };

  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="mt-2 text-3xl font-bold">{value}</h3>
        </div>
        <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${colors[color]}`}>
          <Icon size={24} />
        </div>
      </div>
    </div>
  );
}
